import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, DatePicker, Dropdown, Grid, Input, List, Modal, Row, Select, Space, Statistic, Switch, Table, Tooltip, Typography, message } from "antd";
import { DeleteOutlined, EyeOutlined, FilePdfOutlined, MoreOutlined, PlusOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import debounce from "lodash/debounce";
import dayjs from "dayjs";
import AccesoRestringido from "../AccesoRestringido/AccesoRestringido";
import "./ManifestacionesInteres.css";
import usePermisosManifestacion from "./usePermisosManifestacion";
import EstadoManifestacion from "./components/EstadoManifestacion";
import GenerarManifestacionDrawer from "./components/GenerarManifestacionDrawer";
import DetalleManifestacionDrawer from "./components/DetalleManifestacionDrawer";
import Header160 from "../header/header";
import VisorPdfModal from "./components/VisorPdfModal";
import {
  eliminarManifestacion,
  generarNuevaVersion,
  getDetalle,
  getManifestaciones,
  getProyectos,
  getResumen,
  mensajeDeError,
} from "../../services/manifestacionesInteres/manifestacionesInteresService";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const TAMANO_PAGINA = 20;
const ESTADOS = [
  { value: "PENDIENTE_CARGA", label: "Pendiente de carga" },
  { value: "ACTA_CARGADA", label: "Acta cargada" },
];

const formatoFecha = (valor) => (valor ? dayjs(valor).format("DD/MM/YYYY") : "—");
const rango = (fechas, desde, hasta) =>
  fechas ? { [desde]: fechas[0].format("YYYY-MM-DD"), [hasta]: fechas[1].format("YYYY-MM-DD") } : {};

export default function ManifestacionesInteresPage() {
  const permisos = usePermisosManifestacion();
  const pantallas = Grid.useBreakpoint();

  const [filas, setFilas] = useState([]);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState({ total: 0, pendientes: 0, cargadas: 0 });
  const [cargando, setCargando] = useState(false);
  const [proyectos, setProyectos] = useState([]);

  const [filtros, setFiltros] = useState({});
  const [fechasGeneracion, setFechasGeneracion] = useState(null);
  const [fechasCarga, setFechasCarga] = useState(null);
  const [eliminadas, setEliminadas] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [tamano, setTamano] = useState(TAMANO_PAGINA);
  const [orden, setOrden] = useState({});

  const [generarAbierto, setGenerarAbierto] = useState(false);
  const [detalle, setDetalle] = useState({ oid: null, pestana: "datos" });
  const [visor, setVisor] = useState(null);
  const [motivoEliminar, setMotivoEliminar] = useState({ fila: null, motivo: "", guardando: false });

  const parametros = useMemo(() => ({
    ...Object.fromEntries(Object.entries(filtros).filter(([, v]) => v)),
    ...rango(fechasGeneracion, "generacion_desde", "generacion_hasta"),
    ...rango(fechasCarga, "carga_desde", "carga_hasta"),
    ...(eliminadas ? { eliminadas: "1" } : {}),
  }), [filtros, fechasGeneracion, fechasCarga, eliminadas]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [listado, indicadores] = await Promise.all([
        getManifestaciones({ ...parametros, page: pagina, pageSize: tamano, ...orden }),
        getResumen(parametros),
      ]);
      setFilas(listado.datos);
      setTotal(listado.total);
      setResumen(indicadores);
    } catch (e) {
      message.error(await mensajeDeError(e, "No se pudieron cargar las manifestaciones."));
    } finally {
      setCargando(false);
    }
  }, [parametros, pagina, tamano, orden]);

  useEffect(() => {
    if (permisos.consultar) cargar();
  }, [permisos.consultar, cargar]);

  useEffect(() => {
    if (!permisos.consultar) return;
    getProyectos()
      .then(setProyectos)
      .catch(async (e) => message.error(await mensajeDeError(e, "No se pudieron cargar los proyectos.")));
  }, [permisos.consultar]);

  const aplicarFiltro = useCallback((cambio) => {
    setPagina(1);
    setFiltros((actual) => ({ ...actual, ...cambio }));
  }, []);

  const filtroConEspera = useMemo(() => debounce(aplicarFiltro, 350), [aplicarFiltro]);
  useEffect(() => () => filtroConEspera.cancel(), [filtroConEspera]);

  // Los documentos se piden al abrir: el listado no los trae para no pesar.
  const abrirUltimoDocumento = async (fila, tipo) => {
    try {
      const datos = await getDetalle(fila.oid);
      const documento = datos.documentos.find((d) => d.tipo === tipo);
      if (documento) setVisor({ oid: fila.oid, documento });
      else message.info("No hay documentos de este tipo.");
    } catch (e) {
      message.error(await mensajeDeError(e, "No se pudo abrir el documento."));
    }
  };

  const generar = (fila) => {
    Modal.confirm({
      title: "¿Generar una nueva versión del PDF?",
      content: "Las versiones anteriores y las actas firmadas se conservan.",
      okText: "Generar",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await generarNuevaVersion(fila.oid);
          message.success("Nueva versión generada.");
          cargar();
        } catch (e) {
          message.error(await mensajeDeError(e, "No se pudo generar la nueva versión."));
        }
      },
    });
  };

  const confirmarEliminar = async () => {
    setMotivoEliminar((m) => ({ ...m, guardando: true }));
    try {
      await eliminarManifestacion(motivoEliminar.fila.oid, motivoEliminar.motivo);
      message.success("Manifestación eliminada. Sus documentos se conservan.");
      setMotivoEliminar({ fila: null, motivo: "", guardando: false });
      cargar();
    } catch (e) {
      message.error(await mensajeDeError(e, "No se pudo eliminar la manifestación."));
      setMotivoEliminar((m) => ({ ...m, guardando: false }));
    }
  };

  const opcionesMenu = (fila) => {
    const eliminada = !!fila.eliminado_en;
    return [
      { key: "generada", icon: <FilePdfOutlined />, label: "Ver PDF generado", onClick: () => abrirUltimoDocumento(fila, "GENERADA") },
      fila.estado === "ACTA_CARGADA" && { key: "firmada", icon: <FilePdfOutlined />, label: "Ver acta firmada", onClick: () => abrirUltimoDocumento(fila, "FIRMADA") },
      permisos.generar && !eliminada && { key: "regenerar", icon: <ReloadOutlined />, label: "Generar nuevamente", onClick: () => generar(fila) },
      permisos.historial && { key: "historial", label: "Historial", onClick: () => setDetalle({ oid: fila.oid, pestana: "historial" }) },
      permisos.eliminar && !eliminada && { type: "divider" },
      permisos.eliminar && !eliminada && { key: "eliminar", danger: true, icon: <DeleteOutlined />, label: "Eliminar", onClick: () => setMotivoEliminar({ fila, motivo: "", guardando: false }) },
    ].filter(Boolean);
  };

  const acciones = (fila) => (
    <Space size={4}>
      <Tooltip title="Ver detalles">
        <Button size="small" aria-label="Ver detalles" icon={<EyeOutlined />} onClick={() => setDetalle({ oid: fila.oid, pestana: "datos" })} />
      </Tooltip>
      {permisos.cargar && !fila.eliminado_en && (
        <Tooltip title="Cargar acta firmada">
          <Button size="small" aria-label="Cargar acta firmada" icon={<UploadOutlined />} onClick={() => setDetalle({ oid: fila.oid, pestana: "documentos" })} />
        </Tooltip>
      )}
      <Dropdown trigger={["click"]} menu={{ items: opcionesMenu(fila) }}>
        <Button size="small" aria-label="Más acciones" icon={<MoreOutlined />} />
      </Dropdown>
    </Space>
  );

  const columnas = [
    { title: "Proyecto", dataIndex: "proyecto", key: "proyecto", sorter: true, width: 260, render: (v, f) => <div style={{ whiteSpace: "normal" }}>{f.proyecto_codigo} - {v}</div> },
    { title: "Participante", dataIndex: "nombre_participante", key: "participante", sorter: true, render: (v) => <div style={{ whiteSpace: "normal" }}>{v}</div> },
    { title: "Identificación", dataIndex: "numero_identificacion", key: "identificacion", sorter: true, width: 130 },
    { title: "Generación", dataIndex: "fecha_generacion", key: "fecha_generacion", sorter: true, width: 120, render: formatoFecha },
    { title: "Estado", dataIndex: "estado", key: "estado", sorter: true, width: 160, render: (v, f) => <EstadoManifestacion estado={v} eliminada={!!f.eliminado_en} /> },
    { title: "Carga", dataIndex: "fecha_carga", key: "fecha_carga", sorter: true, width: 120, render: formatoFecha },
    { title: "Creó", dataIndex: "creado_por_login", key: "creado_por", width: 120 },
    { title: "Acciones", key: "acciones", width: 120, fixed: "right", render: (_, f) => acciones(f) },
  ];

  const alCambiarTabla = (paginacion, _f, sorter) => {
    setPagina(paginacion.current);
    setTamano(paginacion.pageSize);
    setOrden(sorter.order ? { sortField: sorter.columnKey, sortOrder: sorter.order === "ascend" ? "asc" : "desc" } : {});
  };

  const paginacion = {
    current: pagina,
    pageSize: tamano,
    total,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    locale: { items_per_page: "/ página" },
    showTotal: (t) => (t === 1 ? "1 manifestación" : `${t} manifestaciones`),
  };

  if (!permisos.consultar) return <AccesoRestringido />;

  return (
    <div className="container-fluid px-3 px-md-4 mb-5">
      <div className="header-filter-container d-flex align-items-center justify-content-between mb-3" style={{ flexWrap: "wrap", gap: 16 }}>
        <Header160 />
        <Col flex="auto" style={{ textAlign: "center", minWidth: 280 }}>
          <Space direction="vertical" size={0} align="center">
            <Title
              level={3}
              style={{
                margin: 0,
                color: "#042956",
                fontWeight: 600
              }}
            >
              Manifestaciones de Interés
            </Title>

            <Space style={{ marginTop: 10 }}>
              {permisos.crear && permisos.generar && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setGenerarAbierto(true)}>
                  Generar manifestación de interés
                </Button>
              )}
            </Space>
          </Space>
        </Col>


      </div>

      <Row gutter={[12, 12]} className="mb-3">
        <Col xs={24} sm={8}><Card size="small"><Statistic title="Total de manifestaciones" value={resumen.total} /></Card></Col>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Pendientes de carga" value={resumen.pendientes} valueStyle={{ color: "#d46b08" }} /></Card></Col>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Actas cargadas" value={resumen.cargadas} valueStyle={{ color: "#389e0d" }} /></Card></Col>
      </Row>

      <Card size="small" className="mb-3">
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12} lg={8}>
            <Select
              allowClear
              showSearch
              aria-label="Proyecto"
              placeholder="Proyecto"
              optionFilterProp="label"
              style={{ width: "100%" }}
              onChange={(v) => aplicarFiltro({ oid_proyecto: v })}
              options={proyectos.map((p) => ({ value: p.oid, label: `${p.codigo} - ${p.proyecto}` }))}
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Input allowClear aria-label="Participante" placeholder="Participante" onChange={(e) => filtroConEspera({ participante: e.target.value })} />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Input allowClear aria-label="Número de identificación" placeholder="Número de identificación" onChange={(e) => filtroConEspera({ identificacion: e.target.value })} />
          </Col>
          <Col xs={24} md={12} lg={6}>
            <Select allowClear aria-label="Estado" placeholder="Estado" style={{ width: "100%" }} options={ESTADOS} onChange={(v) => aplicarFiltro({ estado: v })} />
          </Col>
          <Col xs={24} md={12} lg={7}>
            <RangePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              placeholder={["Generación desde", "hasta"]}
              onChange={(f) => { setPagina(1); setFechasGeneracion(f); }}
            />
          </Col>
          <Col xs={24} md={12} lg={7}>
            <RangePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              placeholder={["Carga desde", "hasta"]}
              onChange={(f) => { setPagina(1); setFechasCarga(f); }}
            />
          </Col>
          {permisos.eliminar && (
            <Col xs={24} md={12} lg={4} className="d-flex align-items-center gap-2">
              <Switch id="ver-eliminadas" size="small" checked={eliminadas} onChange={(v) => { setPagina(1); setEliminadas(v); }} />
              <label htmlFor="ver-eliminadas" className="mb-0">Ver eliminadas</label>
            </Col>
          )}
        </Row>
      </Card>

      {pantallas.md ? (
        <Table
          className="tabla-manifestaciones"
          locale={{ emptyText: "No hay manifestaciones con estos filtros" }}
          rowKey="oid"
          size="middle"
          loading={cargando}
          columns={columnas}
          dataSource={filas}
          pagination={paginacion}
          onChange={alCambiarTabla}
          scroll={{ x: 1050 }}
        />
      ) : (
        <List
          loading={cargando}
          dataSource={filas}
          rowKey="oid"
          pagination={{ ...paginacion, size: "small", showSizeChanger: false, onChange: setPagina }}
          renderItem={(f) => (
            <Card size="small" className="mb-2">
              <div className="d-flex justify-content-between gap-2 mb-1">
                <b>{f.nombre_participante}</b>
                <EstadoManifestacion estado={f.estado} eliminada={!!f.eliminado_en} />
              </div>
              <Text type="secondary" style={{ display: "block" }}>{f.numero_identificacion}</Text>
              <Text style={{ display: "block" }}>{f.proyecto_codigo} - {f.proyecto}</Text>
              <Text type="secondary" style={{ display: "block", fontSize: 13 }} className="mb-2">
                Generación: {formatoFecha(f.fecha_generacion)} · Carga: {formatoFecha(f.fecha_carga)}
              </Text>
              {acciones(f)}
            </Card>
          )}
        />
      )}

      <GenerarManifestacionDrawer
        open={generarAbierto}
        onClose={() => setGenerarAbierto(false)}
        onGenerada={cargar}
        onVerDocumento={(oid, documento) => setVisor({ oid, documento })}
        onVerExistente={(oid) => {
          setGenerarAbierto(false);
          setDetalle({ oid, pestana: "datos" });
        }}
      />

      <DetalleManifestacionDrawer
        oid={detalle.oid}
        pestana={detalle.pestana}
        permisos={permisos}
        onClose={() => setDetalle({ oid: null, pestana: "datos" })}
        onCambio={cargar}
        onVerDocumento={(oid, documento) => setVisor({ oid, documento })}
      />

      <VisorPdfModal
        oidManifestacion={visor?.oid}
        documento={visor?.documento}
        puedeDescargar={permisos.descargar}
        onClose={() => setVisor(null)}
      />

      <Modal
        open={!!motivoEliminar.fila}
        title="Eliminar manifestación"
        okText="Eliminar"
        okButtonProps={{ danger: true, disabled: !motivoEliminar.motivo.trim() }}
        cancelText="Cancelar"
        confirmLoading={motivoEliminar.guardando}
        onOk={confirmarEliminar}
        onCancel={() => setMotivoEliminar({ fila: null, motivo: "", guardando: false })}
      >
        <p>
          Se ocultará la manifestación de {motivoEliminar.fila?.nombre_participante}. Sus documentos se conservan
          y puede restaurarse.
        </p>
        <label htmlFor="motivo-eliminar" className="mb-1">Motivo</label>
        <Input.TextArea
          id="motivo-eliminar"
          rows={3}
          maxLength={500}
          value={motivoEliminar.motivo}
          onChange={(e) => setMotivoEliminar((m) => ({ ...m, motivo: e.target.value }))}
        />
      </Modal>
    </div>
  );
}
